import { useCallback, useEffect, useMemo, useState } from "react";

import { type UseQueryResult, useQueries } from "@tanstack/react-query";

import { useGetIsSubscribedToMarketData, useRequest } from "~/v2/api/hooks";
import { queryKeys } from "~/v2/api/queryKeys";
import { getPortfolioHistory } from "~/v2/api/rest/portfolio";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import { portfolioComparisonQueryKeys } from "~/v3-poc/api/portfolioComparison";
import {
  useWorkspace,
  useWorkspaceStore,
} from "~/v3-poc/features/workspace/shared/workspace/Workspace.context";
import { debugPortfolioHistory } from "./debug";
import {
  clipPortfolioHistoryToMonths,
  type PortfolioComparisonChartSeries,
  type PortfolioComparisonColor,
  type PortfolioHistoryChartPoint,
  toPortfolioComparisonChartData,
  toPortfolioHistoryChartData,
} from "./portfolioHistory";
import { getPortfolioComparisonHistory } from "./portfolioHistoryDatafeed";
import {
  DEFAULT_PORTFOLIO_LOOKBACK,
  getPortfolioLookbackOption,
  type PortfolioLookback,
  type PortfolioLookbackOption,
} from "./portfolioHistoryTimeSettings";

export interface UsePortfolioHistoryChartControllerParams {
  id: string;
}

export interface PortfolioHistoryChartControllerValue {
  comparisonSearchValue: string;
  comparisons: PortfolioComparisonChartSeries[];
  data: PortfolioHistoryChartPoint[];
  hasError: boolean;
  isComparisonFetching: boolean;
  isLoading: boolean;
  isPortfolioFetching: boolean;
  lookback: PortfolioLookback;
  onComparisonSearchValueChange: (value: string) => void;
  onComparisonSelect: (symbol: string) => void;
  onLookbackChange: (lookback: PortfolioLookback) => void;
  onRefresh: () => void;
  onRemoveComparison: (symbol: string) => void;
  percentageData: PortfolioHistoryChartPoint[];
  shouldRenderChart: boolean;
}

const PORTFOLIO_HISTORY_GC_TIME_MS = 5 * 60 * 1_000;
const PORTFOLIO_HISTORY_STALE_TIME_MS = 2 * 60 * 1_000;

/** One color per comparison, so its length is also the comparison cap. */
export const COMPARISON_COLORS: readonly PortfolioComparisonColor[] = [
  "blue",
  "green",
  "orange",
  "purple",
  "pink",
  "teal",
];

interface ComparisonSelection {
  color: PortfolioComparisonColor;
  symbol: string;
}

/** Stable fallback: a fresh `[]` per render would re-run `combine` every time. */
const NO_COMPARISONS: readonly ComparisonSelection[] = [];

interface ComparisonQueryParams {
  isSubscriptionLoading: boolean;
  isSubscribed: boolean;
  lookback: PortfolioLookback;
  portfolioEnd?: number;
  portfolioStart?: number;
  resolution: PortfolioLookbackOption["comparisonResolution"];
  selection: ComparisonSelection;
}

const comparisonQueryOptions = ({
  isSubscriptionLoading,
  isSubscribed,
  lookback,
  portfolioEnd,
  portfolioStart,
  resolution,
  selection,
}: ComparisonQueryParams) => ({
  queryKey: portfolioComparisonQueryKeys.range({
    symbol: selection.symbol,
    lookback,
    resolution,
    from: portfolioStart,
    to: portfolioEnd,
    subscribed: isSubscribed,
  }),
  queryFn: () => {
    if (portfolioStart == null || portfolioEnd == null) {
      throw new Error("Portfolio comparison range is unavailable");
    }
    return getPortfolioComparisonHistory({
      from: portfolioStart,
      isStockDataSubscribed: isSubscribed,
      resolution,
      symbol: selection.symbol,
      to: portfolioEnd,
    });
  },
  enabled:
    portfolioStart != null && portfolioEnd != null && !isSubscriptionLoading,
  gcTime: PORTFOLIO_HISTORY_GC_TIME_MS,
  staleTime: PORTFOLIO_HISTORY_STALE_TIME_MS,
  meta: { showErrorToast: false },
});

export const usePortfolioHistoryChartController = ({
  id,
}: UsePortfolioHistoryChartControllerParams): PortfolioHistoryChartControllerValue => {
  const { currentAccount, product } = useAccountContext();
  const [lookback, setLookback] = useState<PortfolioLookback>(
    DEFAULT_PORTFOLIO_LOOKBACK,
  );
  const storedSettings = useWorkspace((state) => state.widgetSettings[id]);
  const comparisonSelections: readonly ComparisonSelection[] =
    storedSettings?.kind === "portfolioHistory"
      ? storedSettings.comparisons
      : NO_COMPARISONS;
  const workspaceStore = useWorkspaceStore();
  const [comparisonSearchValue, setComparisonSearchValue] = useState("");
  const lookbackOption = getPortfolioLookbackOption(lookback);
  const { isSubscribed, isLoading: isSubscriptionLoading } =
    useGetIsSubscribedToMarketData();
  const {
    data: portfolio,
    isFetching: isPortfolioFetching,
    isLoading,
    isError,
    refetch: refetchPortfolio,
  } = useRequest(
    queryKeys.portfolioHistory({
      accountID: currentAccount?.id ?? "",
      product: product ?? "",
      period: lookbackOption.period,
      timeframe: lookbackOption.timeframe,
      intradayReporting: lookbackOption.intradayReporting,
    }),
    () =>
      getPortfolioHistory({
        accountID: currentAccount?.id ?? "",
        product: product ?? "",
        period: lookbackOption.period,
        timeframe: lookbackOption.timeframe,
        intraday_reporting: lookbackOption.intradayReporting,
      }),
    {
      enabled: Boolean(currentAccount && product),
      gcTime: PORTFOLIO_HISTORY_GC_TIME_MS,
      staleTime: PORTFOLIO_HISTORY_STALE_TIME_MS,
      meta: { showErrorToast: false },
    },
  );
  const data = useMemo(
    () =>
      clipPortfolioHistoryToMonths(
        toPortfolioHistoryChartData(portfolio ?? {}),
        lookbackOption.historyMonths,
      ),
    [lookbackOption.historyMonths, portfolio],
  );
  const portfolioStart = data[0]?.time;
  const portfolioEnd = data.at(-1)?.time;
  const comparisonRequestParams = {
    isSubscriptionLoading,
    isSubscribed,
    lookback,
    portfolioEnd,
    portfolioStart,
    resolution: lookbackOption.comparisonResolution,
  };
  // Memoized so `combine` re-runs only when a selection or the subscription
  // state changes. Referential stability of the returned `series` comes from
  // react-query's structural sharing of the combined value, which is what
  // keeps the identity-keyed chart effects from firing every render.
  const combineComparisons = useCallback(
    (results: UseQueryResult<PortfolioHistoryChartPoint[], Error>[]) => ({
      series: comparisonSelections.map((selection, index) => ({
        ...selection,
        data: results[index]?.data ?? [],
        hasError: results[index]?.isError ?? false,
        isLoading:
          isSubscriptionLoading || (results[index]?.isLoading ?? false),
      })) satisfies PortfolioComparisonChartSeries[],
      isFetching: results.some((result) => result.isFetching),
    }),
    [comparisonSelections, isSubscriptionLoading],
  );
  const comparisons = useQueries({
    queries: comparisonSelections.map((selection) =>
      comparisonQueryOptions({ ...comparisonRequestParams, selection }),
    ),
    combine: combineComparisons,
  });
  const comparisonChartData = useMemo(
    () => toPortfolioComparisonChartData(data, comparisons.series),
    [data, comparisons.series],
  );
  const shouldRenderChart = !isLoading && !isError && data.length > 0;

  const updateComparisonSelections = (
    update: (current: ComparisonSelection[]) => ComparisonSelection[],
  ) => {
    const state = workspaceStore.getState();
    const settings = state.widgetSettings[id];
    const current =
      settings?.kind === "portfolioHistory" ? settings.comparisons : [];
    state.setPortfolioHistoryComparisons(id, update(current));
  };

  const onComparisonSelect = (symbol: string) => {
    updateComparisonSelections((current) => {
      if (current.some((selection) => selection.symbol === symbol)) {
        return current;
      }
      if (current.length === COMPARISON_COLORS.length) {
        return current;
      }
      const usedColors = new Set(current.map(({ color }) => color));
      const color =
        COMPARISON_COLORS.find((candidate) => !usedColors.has(candidate)) ??
        COMPARISON_COLORS[0];
      return [...current, { color, symbol }];
    });
    setComparisonSearchValue("");
  };

  useEffect(() => {
    debugPortfolioHistory("request state", {
      accountId: currentAccount?.id,
      product,
      isLoading,
      isError,
      rawPointCount: portfolio?.timestamp.length ?? 0,
      chartPointCount: data.length,
      lookback,
    });
  }, [
    currentAccount?.id,
    data.length,
    isError,
    isLoading,
    lookback,
    portfolio,
    product,
  ]);

  return {
    comparisonSearchValue,
    comparisons: comparisonChartData.comparisonSeries,
    data,
    hasError: isError,
    isComparisonFetching: comparisons.isFetching,
    isLoading,
    isPortfolioFetching,
    lookback,
    onComparisonSearchValueChange: setComparisonSearchValue,
    onComparisonSelect,
    onLookbackChange: setLookback,
    onRefresh: () => void refetchPortfolio(),
    onRemoveComparison: (symbol) =>
      updateComparisonSelections((current) =>
        current.filter((selection) => selection.symbol !== symbol),
      ),
    percentageData: comparisonChartData.portfolioPercentageData,
    shouldRenderChart,
  };
};
